import React, { useState, useEffect, useCallback } from "react";
import { BiRefresh, BiDownload, BiCalendar, BiFilter, BiMoney, BiTrendingUp, BiUser } from "react-icons/bi";
import { useAnalyticsStore } from "../../../state/analytics";
import MetricCard from "../../../components/Analytics/MetricCard";
import LineChart from "../../../components/Charts/LineChart";
import BarChart from "../../../components/Charts/BarChart";
import showToast from "../../../utils/toastUtils";
import { formatChargesCurrency } from "../../../utils/analyticsHelpers";
import { ThemeContext } from "../../../themes/ThemeContext";
import TableSection from "../../Dashboards/components/TableSection";

const ChargesAnalytics = () => {
  const theme = React.useContext(ThemeContext);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState("");
  const [partners, setPartners] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [chargesTableFilters, setChargesTableFilters] = useState({
    date_range: '',
    partner: '',
    sort_by: 'date',
    sort_order: 'desc',
  });

  const {
    chargesData,
    chargesLoading,
    chargesError,
    fetchChargesSummary,
    exportChargesData,
  } = useAnalyticsStore();

  // Load all charges data when component mounts (no date restrictions)
  useEffect(() => {
    loadChargesData();
  }, []);

  const loadChargesData = useCallback(async () => {
    try {
      // Reset to page 1 when loading new data
      setCurrentPage(1);
      // Fetch all data without date restrictions, with pagination
      const result = await fetchChargesSummary(null, null, selectedPartner || null, 1, perPage);
    } catch (error) {
      // console.error("❌ Failed to load charges data:", error);
      showToast("error", "Failed to load charges data", 3000);
    }
  }, [selectedPartner, perPage, fetchChargesSummary]);

  // Fetch charges data when partner changes
  useEffect(() => {
    if (selectedPartner !== undefined) {
      loadChargesData();
    }
  }, [selectedPartner]);

  const handleDateFilter = async () => {
    if (startDate && endDate) {
      try {
        setCurrentPage(1); // Reset to page 1 when filtering
        await fetchChargesSummary(startDate, endDate, selectedPartner || null, 1, perPage);
        showToast("success", "Data filtered by date range", 2000);
      } catch (error) {
        showToast("error", "Failed to filter data", 3000);
      }
    } else {
      showToast("error", "Please select both start and end dates", 3000);
    }
  };

  const handleClearFilters = async () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedPartner("");
    setCurrentPage(1); // Reset to page 1 when clearing filters
    // Load all data without filters
    try {
      await fetchChargesSummary(null, null, null, 1, perPage);
      showToast("success", "Filters cleared, showing all data", 2000);
    } catch (error) {
      showToast("error", "Failed to clear filters", 3000);
    }
  };

  const handleExport = async () => {
    try {
      // Export with current filters if they exist, otherwise export all data
      const exportStartDate = startDate || null;
      const exportEndDate = endDate || null;
      const exportPartner = selectedPartner || null;
      
      await exportChargesData(exportStartDate, exportEndDate, exportPartner, currentPage, perPage);
      showToast("success", "Charges data exported as Excel file successfully", 2000);
    } catch (error) {
      showToast("error", "Failed to export charges data", 3000);
    }
  };

  const handleRefresh = async () => {
    await loadChargesData();
    showToast("success", "Charges data refreshed successfully", 2000);
  };

  const handlePageChange = async (page) => {
    setCurrentPage(page);
    try {
      await fetchChargesSummary(startDate, endDate, selectedPartner || null, page, perPage);
      showToast("success", `Data loaded for page ${page}`, 2000);
    } catch (error) {
      showToast("error", "Failed to load data for the selected page", 3000);
    }
  };

  const handlePerPageChange = async (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to page 1 when changing per page
    try {
      await fetchChargesSummary(startDate, endDate, selectedPartner || null, 1, newPerPage);
      showToast("success", `Records per page changed to ${newPerPage}`, 2000);
    } catch (error) {
      showToast("error", "Failed to change records per page", 3000);
    }
  };

  // Extract unique partners from detailed charges for filter dropdown
  useEffect(() => {
    
    if (chargesData?.detailed_charges) {
      
      // Handle both array and object formats for detailed_charges
      let chargesArray = [];
      if (Array.isArray(chargesData.detailed_charges)) {
        chargesArray = chargesData.detailed_charges;
      } else if (typeof chargesData.detailed_charges === 'object' && chargesData.detailed_charges !== null) {
        // Convert object with numeric keys to array
        chargesArray = Object.values(chargesData.detailed_charges);
      }
      
      if (chargesArray.length > 0) {
        // Filter to only show PART-* partner IDs as per documentation
        const uniquePartners = [...new Set(
          chargesArray
            .map(tx => tx.partner_id)
            .filter(partnerId => partnerId && partnerId !== 'N/A' && partnerId.startsWith('PART-'))
        )];
        setPartners(uniquePartners);
      } else {
        // console.warn("⚠️ No valid charges data found");
        setPartners([]);
      }
    } else {
    }
  }, [chargesData]);

  // Prepare chart data for revenue trends
  const prepareRevenueChartData = () => {
    if (!chargesData?.detailed_charges) {
      // console.warn("No detailed charges data found");
      return { labels: [], datasets: [] };
    }
    
    // Handle both array and object formats for detailed_charges
    let chargesArray = [];
    if (Array.isArray(chargesData.detailed_charges)) {
      chargesArray = chargesData.detailed_charges;
    } else if (typeof chargesData.detailed_charges === 'object' && chargesData.detailed_charges !== null) {
      // Convert object with numeric keys to array
      chargesArray = Object.values(chargesData.detailed_charges);
    }
    
    if (chargesArray.length === 0) {
      // console.warn("No charges data to process");
      return { labels: [], datasets: [] };
    }
    
    // Debug: Log the first few transactions to see the structure
    
    const dailyData = {};
    chargesArray.forEach((tx, index) => {
      
      // Safely extract and parse the charge amount
      let chargeAmount = 0;
      if (typeof tx.charge === 'string') {
        chargeAmount = parseFloat(tx.charge.replace(/,/g, ''));
      } else if (typeof tx.charge === 'number') {
        chargeAmount = tx.charge;
      } else {
        // console.warn(`Invalid charge value for transaction ${index}:`, tx.charge);
        chargeAmount = 0;
      }
      
      if (isNaN(chargeAmount)) {
        // console.warn(`Could not parse charge amount for transaction ${index}:`, tx.charge);
        chargeAmount = 0;
      }
      
      const date = tx.date ? tx.date.split(' ')[0] : 'unknown';
      if (!dailyData[date]) {
        dailyData[date] = { pay_in_transaction: 0, pay_out: 0, direct_pay_in: 0 };
      }
      
      if (tx.charge_type === 'pay_in_transaction') {
        dailyData[date].pay_in_transaction += chargeAmount;
      } else if (tx.charge_type === 'pay_out') {
        dailyData[date].pay_out += chargeAmount;
      } else if (tx.charge_type === 'direct_pay_in') {
        dailyData[date].direct_pay_in += chargeAmount;
      }
    });

    const sortedDates = Object.keys(dailyData).sort();
    
    return {
      labels: sortedDates.map(date => new Date(date).toLocaleDateString()),
      datasets: [
        {
          label: 'Pay-in Transaction Revenue',
          data: sortedDates.map(date => dailyData[date].pay_in_transaction),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: false,
        },
        {
          label: 'Pay-out Revenue',
          data: sortedDates.map(date => dailyData[date].pay_out),
          borderColor: '#EF4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.4,
          fill: false,
        },
        {
          label: 'Direct Pay-in Revenue',
          data: sortedDates.map(date => dailyData[date].direct_pay_in),
          borderColor: '#8B5CF6',
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          tension: 0.4,
          fill: false,
        },
        {
          label: 'Total Revenue',
          data: sortedDates.map(date => 
            dailyData[date].pay_in_transaction + 
            dailyData[date].pay_out + 
            dailyData[date].direct_pay_in
          ),
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4,
          fill: false,
        }
      ]
    };
  };

  // Prepare chart data for charge type distribution
  const prepareChargeTypeChartData = () => {
    if (!chargesData?.summary) return { labels: [], datasets: [] };
    
    // Safely extract and parse values with null checks and defaults
    const payInTotalStr = chargesData.summary.pay_in_charges_total || '0';
    const payOutTotalStr = chargesData.summary.pay_out_charges_total || '0';
    const directPayInTotalStr = chargesData.summary.direct_pay_in_charges_total || '0';
    
    const payInTotal = parseFloat(payInTotalStr.toString().replace(/,/g, '')) || 0;
    const payOutTotal = parseFloat(payOutTotalStr.toString().replace(/,/g, '')) || 0;
    const directPayInTotal = parseFloat(directPayInTotalStr.toString().replace(/,/g, '')) || 0;
    
    return {
      labels: ['Pay-in Transaction Charges', 'Pay-out Charges', 'Direct Pay-in Charges'],
      datasets: [
        {
          label: 'Revenue',
          data: [payInTotal, payOutTotal, directPayInTotal],
          backgroundColor: ['#3B82F6', '#EF4444', '#8B5CF6'],
          borderColor: ['#3B82F6', '#EF4444', '#8B5CF6'],
          borderWidth: 0,
        }
      ]
    };
  };

  if (chargesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3" style={{ color: theme.colors.text.secondary }}>Loading charges data...</p>
        </div>
      </div>
    );
  }

  if (chargesError) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <h3 className="text-xl font-semibold mb-2" style={{ color: theme.colors.text.primary }}>
          Error Loading Charges Data
        </h3>
        <p className="text-gray-400 mb-4">{chargesError}</p>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const summary = chargesData?.summary;
  const detailedCharges = chargesData?.detailed_charges || [];
  const counts = chargesData?.counts;

  // Ensure detailedCharges is an array, handling both array and object formats
  let safeDetailedCharges = [];
  if (Array.isArray(detailedCharges)) {
    safeDetailedCharges = detailedCharges;
  } else if (typeof detailedCharges === 'object' && detailedCharges !== null) {
    // Convert object with numeric keys to array
    safeDetailedCharges = Object.values(detailedCharges);
  }

  // Custom MetricCard for charges with proper currency formatting
  const ChargesMetricCard = ({ title, value, icon: Icon, iconColor, subtitle, format = "currency" }) => {
    let formattedValue;
    
    if (format === "currency") {
      formattedValue = formatChargesCurrency(value);
    } else if (format === "number") {
      formattedValue = value?.toLocaleString() || "0";
    } else {
      formattedValue = value || "0";
    }
    
    return (
      <div 
        className="flex-1 flex items-center gap-4 rounded-lg p-5 shadow-md"
        style={{
          color: theme.colors.text.primary,
          backgroundColor: theme.colors.background.secondary,
        }}
      >
        {Icon && (
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-xl">
            <Icon className={`text-xl ${iconColor}`} />
          </div>
        )}
        <div className="flex-1">
          <h3 className="text-lg font-bold" style={{ color: theme.colors.text.primary }}>
            {title}
          </h3>
          <p className="text-xl font-semibold" style={{ color: theme.colors.text.primary }}>
            {formattedValue}
          </p>
          {subtitle && (
            <p className="text-sm text-gray-400 mt-1">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    );
  };

  // Define columns for the detailed transactions table
  const chargesTableColumns = [
    {
      key: 'id',
      label: 'ID',
      format: (value) => `#${value}`,
    },
    {
      key: 'date',
      label: 'Date',
      format: (value) => new Date(value).toLocaleDateString(),
    },
    {
      key: 'charge_type',
      label: 'Charge Type',
      format: (value) => {
        const type = value;
        if (type === 'pay_in_transaction') {
          return (
            <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
              Pay-in Transaction
            </span>
          );
        } else if (type === 'pay_out') {
          return (
            <span className="px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800">
              Pay-out
            </span>
          );
        } else {
          return (
            <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
              Direct Pay-in
            </span>
          );
        }
      },
    },
    {
      key: 'amount',
      label: 'Amount',
      format: (value) => `₦${parseFloat(value).toLocaleString()}`,
    },
    {
      key: 'charge',
      label: 'Charge',
      format: (value) => `₦${parseFloat(value).toLocaleString()}`,
    },
    {
      key: 'business_name',
      label: 'Business Name',
      format: (value) => value === 'N/A' ? '-' : value,
    },
    {
      key: 'partner_id',
      label: 'Partner ID',
    },
    {
      key: 'payer_account_name',
      label: 'Payer Account',
      format: (value) => value === 'N/A' ? '-' : value,
    },
    {
      key: 'payer_bank',
      label: 'Payer Bank',
    },
    {
      key: 'transaction_reference',
      label: 'Transaction Ref',
      format: (value) => (
        <span className="font-mono text-xs" title={value}>
          {value.length > 20 ? `${value.substring(0, 20)}...` : value}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      format: (value) => {
        if (value === 'confirmed') {
          return (
            <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
              {value}
            </span>
          );
        } else {
          return (
            <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
              {value}
            </span>
          );
        }
      },
    },
  ];

  // Define filters for the detailed transactions table
  // const [chargesTableFilters, setChargesTableFilters] = useState({
  //   date_range: '',
  //   partner: '',
  //   sort_by: 'date',
  //   sort_order: 'desc',
  // });

  return (
    <div className="space-y-12">
      {/* Header with Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: theme.colors.text.primary }}>
            Charges Analytics
          </h2>
          <p className="text-gray-400">
            View all revenue from transaction charges. Use filters to narrow down data by date range or partner.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            style={{ background: "linear-gradient(270deg, rgb(75, 73, 172) 0.05%, rgb(153, 153, 255) 99.97%)" }}
          >
            <BiDownload className="w-4 h-4" />
            Export Excel
          </button> */}
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            style={{ background: "linear-gradient(270deg, rgb(75, 73, 172) 0.05%, rgb(153, 153, 255) 99.97%)" }}
            title="Refresh data"
          >
            <BiRefresh className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="flex items-center gap-2">
          <BiCalendar className="text-gray-400" />
          <span className="text-sm text-gray-400">Date Range:</span>
        </div>
        <div className="flex gap-2">
          <input
            type="date"
            value={startDate || ''}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white"
            placeholder="Start Date"
          />
          <span className="text-gray-400 self-center">to</span>
          <input
            type="date"
            value={endDate || ''}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white"
            placeholder="End Date"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <BiFilter className="text-gray-400" />
          <span className="text-sm text-gray-400">Partner:</span>
          <select
            value={selectedPartner}
            onChange={(e) => setSelectedPartner(e.target.value)}
            className="px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white"
          >
            <option value="">All Partners</option>
            {partners.map(partner => (
              <option key={partner} value={partner}>{partner}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleDateFilter}
            disabled={!startDate || !endDate}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-500 disabled:cursor-not-allowed"
          >
            Filter by Date
          </button>
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Summary Metrics */}
      {summary && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold" style={{ color: theme.colors.text.primary }}>
            Revenue Summary
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ChargesMetricCard
              title="Total Revenue"
              value={summary?.total_charges || 0}
              format="currency"
              icon={BiMoney}
              iconColor="text-green-500"
              subtitle={`${summary?.currency || 'NGN'} • ${summary?.date_range?.start_date || 'N/A'} to ${summary?.date_range?.end_date || 'N/A'}`}
            />
            <ChargesMetricCard
              title="Pay-in Transaction Revenue"
              value={summary?.pay_in_charges_total || 0}
              format="currency"
              icon={BiTrendingUp}
              iconColor="text-blue-500"
              subtitle={`${counts?.pay_in_transactions || 0} transactions`}
            />
            <ChargesMetricCard
              title="Pay-out Revenue"
              value={summary?.pay_out_charges_total || 0}
              format="currency"
              icon={BiTrendingUp}
              iconColor="text-orange-500"
              subtitle={`${counts?.pay_out_transactions || 0} transactions`}
            />
            <ChargesMetricCard
              title="Direct Pay-in Revenue"
              value={summary?.direct_pay_in_charges_total || 0}
              format="currency"
              icon={BiTrendingUp}
              iconColor="text-purple-500"
              subtitle={`${counts?.direct_pay_in_transactions || 0} transactions`}
            />
          </div>
        </div>
      )}

      {/* Transaction Counts */}
      {counts && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold" style={{ color: theme.colors.text.primary }}>
            Transaction Overview
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ChargesMetricCard
              title="Total Transactions"
              value={counts?.total_transactions || 0}
              format="number"
              icon={BiUser}
              iconColor="text-purple-500"
            />
            <ChargesMetricCard
              title="Pay-in Transactions"
              value={counts?.pay_in_transactions || 0}
              format="number"
              icon={BiUser}
              iconColor="text-blue-500"
            />
            <ChargesMetricCard
              title="Pay-out Transactions"
              value={counts?.pay_out_transactions || 0}
              format="number"
              icon={BiUser}
              iconColor="text-orange-500"
            />
            <ChargesMetricCard
              title="Direct Pay-in Transactions"
              value={counts?.direct_pay_in_transactions || 0}
              format="number"
              icon={BiUser}
              iconColor="text-purple-500"
            />
          </div>
        </div>
      )}

            {/* Pagination Controls */}
            {chargesData?.pagination && (
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.secondary }}>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">
              Showing {((chargesData.pagination.current_page - 1) * chargesData.pagination.per_page) + 1} to {Math.min(chargesData.pagination.current_page * chargesData.pagination.per_page, chargesData.pagination.total_records)} of {chargesData.pagination.total_records} records
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">Records per page:</span>
              <select
                value={perPage}
                onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                className="px-2 py-1 border border-gray-600 rounded bg-gray-700 text-white text-sm"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(chargesData.pagination.previous_page)}
              disabled={!chargesData.pagination.has_previous_page}
              className="px-3 py-1 border border-gray-600 rounded bg-gray-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
            >
              Previous
            </button>
            
            <span className="text-sm text-gray-400">
              Page {chargesData.pagination.current_page} of {chargesData.pagination.total_pages}
            </span>
            
            <button
              onClick={() => handlePageChange(chargesData.pagination.next_page)}
              disabled={!chargesData.pagination.has_next_page}
              className="px-3 py-1 border border-gray-600 rounded bg-gray-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className="space-y-6">
        <h3 className="text-xl font-bold" style={{ color: theme.colors.text.primary }}>
          Analytics & Trends
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="rounded-lg p-6" style={{ backgroundColor: theme.colors.background.secondary }}>
            <h4 className="text-lg font-bold mb-4" style={{ color: theme.colors.text.primary }}>
              Daily Revenue Trends
            </h4>
            {safeDetailedCharges.length > 0 ? (
              <LineChart
                data={prepareRevenueChartData()}
                height={300}
                showLegend={true}
              />
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-400">
                <p>No transaction data available for the selected period</p>
              </div>
            )}
          </div>

          <div className="rounded-lg p-6" style={{ backgroundColor: theme.colors.background.secondary }}>
            <h4 className="text-lg font-bold mb-4" style={{ color: theme.colors.text.primary }}>
              Revenue Distribution
            </h4>
            {chargesData?.summary ? (
              <BarChart
                data={prepareChargeTypeChartData()}
                height={300}
                showLegend={true}
              />
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-400">
                <p>No summary data available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChargesAnalytics;
